import Checkbox from "@mui/joy/Checkbox";
import FormLabel from "@mui/joy/FormLabel";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import { ColDef } from "ag-grid-community";
import { useEffect, useRef, useState } from "react";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import { SxProps } from "@mui/system";

interface Props<T> {
  disabled?: boolean;
  columns: ColDef<T>[];
  changeVisibility: (columnId: string, visible: boolean) => void;
  sx?: SxProps;
}

const ColumnsConfig = <T,>({ disabled, columns, changeVisibility, sx }: Props<T>) => {
  const ref = useRef<HTMLDivElement>(null);

  const [showSidebar, setShowSidebar] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  useEffect(() => {
    setVisibleColumns(columns.filter((column) => !column.hide).map((column) => column.colId! || column.field!));
  }, [columns]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current?.contains(e.target as Node)) {
        // hide the columns sidebar
        setShowSidebar(false);
      }
    };

    document.addEventListener("click", handler);

    return () => {
      document.removeEventListener("click", handler);
    };
  }, []);

  return (
    <>
      {/* toggle button */}
      <Stack
        component={"button"}
        type="button"
        disabled={disabled}
        alignItems={"center"}
        justifyContent={"center"}
        className="noselect"
        sx={{
          bgcolor: showSidebar ? "primary.200" : "#F0F4F8",
          width: "28px",
          py: 4,
          height: "110px",
          border: "1px solid",
          borderColor: "neutral.300",
          borderRadius: "sm",
          cursor: "pointer",
          "&:disabled": {
            opacity: "50%",
            cursor: "not-allowed",
          },
        }}
        onClick={(e) => {
          e.stopPropagation();
          setShowSidebar((prevValue) => !prevValue);
        }}
      >
        <Typography
          sx={{
            transform: "rotate(90deg)",
            color: showSidebar ? "primary.700" : "",
          }}
          fontSize={"sm"}
          startDecorator={
            <CalendarViewMonthIcon
              sx={{
                color: showSidebar ? "primary.600" : "",
              }}
            />
          }
        >
          Columns
        </Typography>
      </Stack>

      {/* backdrop */}
      <Stack
        direction={"row"}
        justifyContent={"flex-end"}
        sx={[
          {
            position: "absolute",
            left: 0,
            p: 2,
            right: 36,
            top: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.3)",
            zIndex: showSidebar ? 20 : -10,
            borderRadius: "md",
            opacity: showSidebar ? 1 : 0,
            transform: "all 1s ease-in-out",
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {/* config */}
        <Stack
          ref={ref}
          sx={{
            minWidth: "230px",
            bgcolor: "white",
            p: 1,
            borderRadius: "sm",
            maxHeight: "100%",
            overflowY: "auto",
          }}
        >
          <Typography
            level="body-sm"
            fontWeight={"lg"}
            sx={{
              mb: 1,
            }}
          >
            Columns
          </Typography>
          {columns.map((column) => (
            <Stack
              key={column.colId || column.field}
              direction={"row"}
              alignItems={"center"}
              gap={1}
              sx={{
                p: 1,
              }}
            >
              <FormLabel>
                <Checkbox
                  size="sm"
                  sx={{
                    mr: 1,
                  }}
                  checked={
                    column.colId ? visibleColumns.includes(column.colId) : visibleColumns.includes(column.field!)
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setVisibleColumns((prevValue) => [...prevValue, column.colId! || column.field!]);
                      changeVisibility(column.colId! || column.field!, true);
                    } else {
                      setVisibleColumns((prevValue) =>
                        prevValue.filter((v) => {
                          if (column.colId) {
                            return v !== column.colId;
                          } else {
                            return v !== column.field;
                          }
                        }),
                      );
                      changeVisibility(column.colId! || column.field!, false);
                    }
                  }}
                />
                {column.headerName}
              </FormLabel>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </>
  );
};

export default ColumnsConfig;
